<template>
  <div class="page-calendar">
    <div style="font-size: 12px; text-align: right;">
      ({{ $t("Timezone") }}: {{ calendarOptions && calendarOptions.timeZone }})
    </div>
    <FullCalendar ref="calendar" :options="calendarOptions">
      <template v-slot:eventContent="arg">
        <b>{{ arg.timeText }}</b>
        <i>{{ arg.event.title }}</i>
      </template>
    </FullCalendar>
    <md-progress-spinner
      md-mode="indeterminate"
      class="custom-page-spinner"
      v-if="loading"
    ></md-progress-spinner>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import frLocale from "@fullcalendar/core/locales/fr";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import {
  getPilotAvailableSchedule,
  bookingUpdate
} from "@/services/flightReview.service.js";
import { DEFAULT_TIMEZONE } from "@/const.js";
import swal from "sweetalert2";
import { secondsToHoursAndMinutes, generateEvents } from "@/helpers/utils";
import moment from "moment-timezone";

export default {
  components: {
    FullCalendar // make the <FullCalendar> tag available
  },
  data: function() {
    const locale = window.localStorage.getItem("locale") || "en";
    return {
      calendarEvents: [],
      calendarOptions: {
        locales: [esLocale, frLocale],
        locale,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          momentTimezonePlugin
        ],
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        },
        initialView: "timeGridWeek",
        events: [],
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        // select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        displayEventTime: false,
        timeZone: DEFAULT_TIMEZONE
      },
      reviewerId: "",
      reviewStartDate: "",
      loading: false
    };
  },
  mounted() {
    this.loadCadendarDate();
  },
  methods: {
    moment: function(date) {
      return moment(date);
    },
    validate() {
      if (!this.reviewStartDate) {
        this.$notify({
          message: "Please select a flight review time",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "warning",
          timeout: 3000
        });
        return;
      }
    },

    handleWeekendsToggle() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends; // update a property
    },
    handleEventClick(clickInfo) {
      if (!clickInfo.el.classList.contains("available-event")) {
        return;
      }
      if (this.loading) return;

      const _self = this;
      swal
        .fire({
          text: "Do you want to reschedule now?",
          showCancelButton: true,
          confirmButtonClass: "md-button md-success",
          cancelButtonClass: "md-button md-danger",
          confirmButtonText: this.$t("Yes"),
          cancelButtonText: this.$t("Cancel"),
          buttonsStyling: false
        })
        .then(async function({ value }) {
          if (!value) return;
          _self.loading = true;
          // reset
          _self.calendarOptions.events.map(item => {
            if (item.classNames.indexOf("booked-current") === -1) {
              item.classNames = ["available-event"];
            }
          });

          _self.calendarOptions.events.map(item => {
            if (+item.start === +clickInfo.event.start) {
              item.classNames = ["booked-future"];
            }
          });

          _self.calendarOptions.events = [..._self.calendarOptions.events];

          try {
            const { id } = _self.$route.query;
            await bookingUpdate({
              id,
              start_date: +clickInfo.event.start / 1000,
              end_date: +clickInfo.event.end / 1000
            });
            _self.loading = false;
            _self.$router.replace("/flight-review/bookingList");
          } catch {
            _self.loading = false;
          }
        });
    },
    async loadCadendarDate() {
      const {
        reviewerId,
        startTime: lastStartTime,
        endTime: lastEndTime,
        packageTitle,
        status
      } = this.$route.query;
      console.log(status);
      if (!reviewerId) return;
      // getPilotAvailableSchedule
      const {
        events,
        schedule = [],
        review_timezone
      } = await getPilotAvailableSchedule(reviewerId);
      let timezone = review_timezone || DEFAULT_TIMEZONE;
      let futureAvailableAll = [];
      for (let item of schedule) {
        let startTime, endTime;
        const availableSchedule = item.available_schedule;
        if (availableSchedule) {
          let weeklyIdx = [];
          delete availableSchedule.repeat;
          for (let key in availableSchedule) {
            let _availableSchedule = availableSchedule[key];
            if (_availableSchedule.available) {
              startTime = secondsToHoursAndMinutes(
                _availableSchedule.start_time
              );
              endTime = secondsToHoursAndMinutes(_availableSchedule.end_time);
              weeklyIdx.push(key);
            }
          }
          const { futureAvailable } = generateEvents({
            daysOfWeek: weeklyIdx,
            startTime,
            endTime,
            startDate: item.start_time.$date,
            endDate: item.end_time.$date,
            events,
            timezone,
            duration: item.duration || 60
          });
          futureAvailableAll = futureAvailableAll.concat(futureAvailable);
        }
      }

      // startTime
      const currentBooked = {
        title: packageTitle,
        start: this.moment(+lastStartTime)
          .tz(timezone)
          .toDate(),
        end: this.moment(+lastEndTime)
          .tz(timezone)
          .toDate(),
        classNames: [+status === 4 ? "cancel-current" : "booked-current"]
      };
      this.calendarOptions.timeZone = timezone;
      this.calendarOptions.events = [...futureAvailableAll, currentBooked];
      // Manually trigger resize for fixing timeslot render issue
      window.dispatchEvent(new Event("resize"));
    }
  }
};
</script>

<style lang="css">
.page-calendar {
  .booked-past {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .booked-future {
    background-color: #3788d8;
    cursor: not-allowed;
  }

  .available-event {
    background-color: #4caf50;
    cursor: pointer;
  }
  .booked-current {
    background-color: rgb(202, 202, 90);
    cursor: not-allowed;
  }
  .cancel-current {
    background-color: #ccc;
    cursor: not-allowed;
  }
}
</style>
